import {TabView} from "primereact/tabview";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {matchPath, Outlet, useLocation} from "react-router-dom";

import AppreciationToolbar from "../component/appreciation/AppreciationToolbar";
import {DownloadMapPdfButton} from "../component/DownloadMapPdfButton";
import {MainFrame, MainToolbar} from "../component/MainFrame";
import MissingPermissionIndicator from "../component/MissingPermissionIndicator";
import {getTabPanelWithPermission} from "../component/TabPanelWithPermission";
import {useNavigateWithQuery} from "../hooks/navigationHooks";
import {selectPermissions} from "../redux/authSlice";
import {selectShowTrafficMap} from "../redux/locationInfoSlice";
import {LocationPdfType, PdfOrientation} from "../redux/pdfConstants";
import {hidePdfPreview} from "../redux/pdfPreviewSlice";
import {useEffectRequiresValidRevaId} from "../util/hooks";
import {
    checkPermissionRule,
    PERMISSION_RULE_CADASTRE,
    PERMISSION_RULE_CLIMATERISK,
    PERMISSION_RULE_EMISSION_TAB,
    PERMISSION_RULE_LOCATION,
    PERMISSION_RULE_LOCATION_MACRO,
    PERMISSION_RULE_LOCATION_MICRO,
    PERMISSION_RULE_LOCATION_POIS,
} from "../util/permissions";

/** @type {TabHeaderWithPermissionParams[]} */
const TABS = [
    {
        route: "points-of-interest",
        headerI18n: "location.category.pois",
        permissionRule: PERMISSION_RULE_LOCATION_POIS,
        appreciationKeyTab: "points_of_interest",
    },
    {
        route: "emission",
        headerI18n: "location.category.noise",
        permissionRule: PERMISSION_RULE_EMISSION_TAB,
        appreciationKeyTab: "noise",
    },
    {
        route: "climaterisk",
        headerI18n: "location.category.climaterisk",
        permissionRule: PERMISSION_RULE_CLIMATERISK,
        appreciationKeyTab: "climaterisk",
    },
    {
        route: "micro",
        headerI18n: "location.category.micro",
        permissionRule: PERMISSION_RULE_LOCATION_MICRO,
        appreciationKeyTab: "micro",
    },
    {
        route: "macro",
        headerI18n: "location.category.macro",
        permissionRule: PERMISSION_RULE_LOCATION_MACRO,
        appreciationKeyTab: "macro",
    },
    {
        route: "cadastre",
        headerI18n: "location.category.cadastre",
        permissionRule: PERMISSION_RULE_CADASTRE,
        appreciationKeyTab: "cadastre",
    },
];
const TAB_ROUTES = TABS.map((tab) => tab.route);

/**
 *
 * @returns {React.JSX.Element}
 */
function PageLocation() {
    const {t} = useTranslation();

    const navigate = useNavigateWithQuery();
    const dispatch = useDispatch();
    const location = useLocation();
    const {pathname} = location;
    const showTrafficMap = useSelector(selectShowTrafficMap);

    // Bei jedem Wechsel eines Lage-Tabs jede offene PDF-Druckvorschau-Box schließen.
    useEffect(() => {
        dispatch(hidePdfPreview());
    }, [pathname, dispatch]);

    const permissions = useSelector(selectPermissions);

    const [appreciationForTabVisible, setAppreciationForTabVisible] = useState(false);

    useEffectRequiresValidRevaId();

    let activeIndex = null;
    for (let index = 0; index < TAB_ROUTES.length; index++) {
        if (matchPath(`/location/${TAB_ROUTES[index]}`, pathname)) {
            activeIndex = index;
            break;
        }
    }

    const onTabChange = (index) => {
        if (index >= 0 && index < TAB_ROUTES.length) {
            setAppreciationForTabVisible(false);
            navigate(`/location/${TAB_ROUTES[index]}`);
        }
    };
    const appreciationKeyTab = TABS[activeIndex].appreciationKeyTab;

    const hasAnyPermissions = checkPermissionRule(permissions, PERMISSION_RULE_LOCATION);
    if (!hasAnyPermissions) {
        return <MissingPermissionIndicator missingPermissions={true} />;
    }

    /** @type React.ReactElement[] */
    const tabs = TABS.map((tab) => getTabPanelWithPermission(tab, t, permissions));

    return (
        <MainFrame>
            <MainToolbar title={t("location.location")}>
                <TabView
                    className="flex-1 HeaderTabView"
                    panelContainerClassName="hidden"
                    activeIndex={activeIndex}
                    onTabChange={(e) => onTabChange(e.index)}
                >
                    {tabs}
                </TabView>
                <AppreciationToolbar
                    page="location"
                    tab={appreciationKeyTab}
                    appreciationVisible={appreciationForTabVisible}
                    setAppreciationVisible={setAppreciationForTabVisible}
                    linkTo={
                        appreciationKeyTab === "micro" || appreciationKeyTab === "macro"
                            ? "/market/description"
                            : undefined
                    }
                />
                {activeIndex === 0 && (
                    <DownloadMapPdfButton
                        tooltip={t("pois.download_pdf_tooltip")}
                        type={showTrafficMap ? LocationPdfType.TRAFFIC : LocationPdfType.NEARBY}
                    />
                )}
                {activeIndex === 3 && (
                    <>
                        <DownloadMapPdfButton
                            tooltip={t("location.printMicroTooltipHorizontal")}
                            orientation={PdfOrientation.HORIZONTAL}
                            type={LocationPdfType.MICRO_LOCATION}
                        />
                        <DownloadMapPdfButton
                            tooltip={t("location.printMicroTooltipVertical")}
                            orientation={PdfOrientation.VERTICAL}
                            type={LocationPdfType.MICRO_LOCATION}
                        />
                    </>
                )}
                {activeIndex === 4 && (
                    <>
                        <DownloadMapPdfButton
                            tooltip={t("location.printMacroTooltipHorizontal")}
                            orientation={PdfOrientation.HORIZONTAL}
                            type={LocationPdfType.MACRO_LOCATION}
                        />
                        <DownloadMapPdfButton
                            tooltip={t("location.printMacroTooltipVertical")}
                            orientation={PdfOrientation.VERTICAL}
                            type={LocationPdfType.MACRO_LOCATION}
                        />
                    </>
                )}
                {activeIndex === 5 && (
                    <>
                        <DownloadMapPdfButton
                            tooltip={t("landregister.printHorizontalTooltip")}
                            orientation={PdfOrientation.HORIZONTAL}
                            type={LocationPdfType.CADASTRE}
                        />
                        <DownloadMapPdfButton
                            tooltip={t("landregister.printVerticalTooltip")}
                            orientation={PdfOrientation.VERTICAL}
                            type={LocationPdfType.CADASTRE}
                        />
                    </>
                )}
            </MainToolbar>
            <Outlet
                context={{
                    appreciation: {
                        page: "location",
                        tab: appreciationKeyTab,
                        visible: appreciationForTabVisible,
                    },
                }}
            />
        </MainFrame>
    );
}

export default PageLocation;
